/**
 * Select/Dropdown Component
 *
 * A reusable dropdown select component with theme support.
 */

import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface SelectProps {
  label?: string;
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function Select({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
}: SelectProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  return (
    <View style={styles.container}>
      {label && <Text style={[styles.label, { color: theme.colors.textSecondary }]}>{label}</Text>}
      <View style={[styles.optionsContainer, { borderColor: theme.colors.border }]}>
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.option,
                { backgroundColor: theme.colors.surface },
                isSelected && [
                  styles.selectedOption,
                  {
                    borderColor: theme.colors.primary,
                    backgroundColor: theme.colors.selectedBackground,
                  },
                ],
              ]}
              onPress={() => onChange(option.value)}
              disabled={disabled}
              activeOpacity={0.7}
            >
              <View style={styles.optionContent}>
                <Text
                  style={[
                    styles.optionLabel,
                    { color: isSelected ? theme.colors.text : theme.colors.text },
                  ]}
                >
                  {option.label}
                </Text>
                {option.description && (
                  <Text style={[styles.optionDescription, { color: theme.colors.textSecondary }]}>
                    {' '}
                    {option.description}
                  </Text>
                )}
                {isSelected && (
                  <Text style={[styles.checkmark, { color: theme.colors.primary }]}>
                    {' \u2713'}
                  </Text>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginBottom: theme.spacing.md,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    optionsContainer: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      overflow: 'hidden',
    },
    option: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      marginBottom: 1,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.divider,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    selectedOption: {
      borderWidth: theme.id === 'bauhaus' ? 3 : 2,
      backgroundColor: theme.colors.selectedBackground,
    },
    optionContent: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
    },
    optionLabel: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    optionDescription: {
      fontSize: theme.typography.fontSizeSm,
      marginLeft: theme.spacing.xs,
    },
    checkmark: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
    },
  });
}
